/**
 * src/pages/Users.tsx
 *
 * Admin /users tab. Read-only inspection of every user record + their
 * signals + recent activity. Backed by /v1/observe/dbview/users{,/:tgId}.
 *
 * Two views:
 *   - List with filters (q / tier / dm_opt_in / onboarded / deliveredWithinDays).
 *   - Click row → drawer with full prefs, signal breakdown (incl. meh —
 *     post-2026-05-08 signals.service supports it), preference vector,
 *     recent deliveries, recent events, badges.
 *
 * No write affordances. The "Coming soon" tags on the action buttons are
 * deliberate placeholders — suspending / re-onboarding / gifting Pro
 * deserve careful review before they ship.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, X as XIcon, User as UserIcon, BellOff, Bell, Crown, Gift, RotateCw } from 'lucide-react';
import {
  listAdminUsers,
  getAdminUser,
  getUserCooldowns,
  type AdminUserListRow,
  type AdminUserDetail,
  type UserCooldownRecord,
} from '@/api/dbview';
import { cn } from '@/lib/cn';
import { relativeTime, txt } from '@/lib/format';

// 2026-05-10 — Tony calibration #3 effective-cap resolver. Mirrors
// `pacing.service.ts::effectiveDmCap()` on the backend. If you tune any
// of these constants or the dm_frequency multipliers, sync both files.
const TIER_CAPS: Record<string, number> = { free: 5, pro: 12, reseller_pro: 25 };
function effectiveDmCap(tier: string | null | undefined, dmFrequency?: string | null): number {
  const base = TIER_CAPS[tier ?? 'free'] ?? TIER_CAPS.free;
  if (dmFrequency === 'off') return 0;
  if (dmFrequency === 'low') return Math.floor(base * 0.4);
  return base;
}

export default function Users() {
  const [q, setQ] = useState('');
  const [tier, setTier] = useState<'' | 'free' | 'pro' | 'reseller_pro'>('');
  const [onboarded, setOnboarded] = useState<'' | 'true'>('');
  const [dmOptIn, setDmOptIn] = useState<'' | 'true' | 'false'>('');
  const [deliveredDays, setDeliveredDays] = useState<'' | '1' | '7' | '30'>('');
  const [openTgId, setOpenTgId] = useState<number | null>(null);

  const filters = useMemo(() => ({
    q: q.trim() || undefined,
    tier: tier || undefined,
    onboarded: onboarded === 'true' ? true : undefined,
    dmOptIn: dmOptIn === 'true' ? true : dmOptIn === 'false' ? false : undefined,
    deliveredWithinDays: deliveredDays ? Number(deliveredDays) : undefined,
  }), [q, tier, onboarded, dmOptIn, deliveredDays]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', filters],
    queryFn:  () => listAdminUsers(filters),
  });

  return (
    <div className="space-y-4 max-w-7xl">
      <header>
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-slate-500 mt-1">
          Admin inspection · click a row for prefs, signals, recent deliveries
        </p>
      </header>

      {/* Filter bar */}
      <div className="rounded border border-slate-800 bg-slate-900/40 p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
        <label className="block">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="username / firstName / tgId"
              className="w-full bg-slate-950 border border-slate-800 rounded pl-7 pr-2 py-1.5 text-sm"
            />
          </div>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Tier</div>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm"
          >
            <option value="">any</option>
            <option value="free">free</option>
            <option value="pro">pro</option>
            <option value="reseller_pro">reseller_pro</option>
          </select>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Onboarded</div>
          <select
            value={onboarded}
            onChange={(e) => setOnboarded(e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm"
          >
            <option value="">any</option>
            <option value="true">yes</option>
          </select>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">DM opt-in</div>
          <select
            value={dmOptIn}
            onChange={(e) => setDmOptIn(e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm"
          >
            <option value="">any</option>
            <option value="true">opted in</option>
            <option value="false">opted out</option>
          </select>
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Delivered in</div>
          <select
            value={deliveredDays}
            onChange={(e) => setDeliveredDays(e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-sm"
          >
            <option value="">any</option>
            <option value="1">last 24h</option>
            <option value="7">last 7d</option>
            <option value="30">last 30d</option>
          </select>
        </label>
      </div>

      {/* List */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
        </div>
      )}
      {isError && (
        <div className="text-sm text-red-400 py-4">Failed to load users.</div>
      )}
      {data && data.rows.length === 0 && (
        <div className="text-sm text-slate-500 py-4">No users match.</div>
      )}
      {data && data.rows.length > 0 && (
        <div className="rounded border border-slate-800 overflow-hidden">
          <div className="px-3 py-2 text-xs text-slate-500 bg-slate-900/40 border-b border-slate-800">
            {data.rows.length} of {data.total} users
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-900/40 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">tgId</th>
                <th className="text-left px-3 py-2">user</th>
                <th className="text-left px-3 py-2">tier</th>
                <th className="text-left px-3 py-2">prefs</th>
                <th className="text-right px-3 py-2">deliveries</th>
                <th className="text-right px-3 py-2">last</th>
                <th className="text-right px-3 py-2">fb 30d</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data.rows.map(row => (
                <UserRow key={row.tgId} row={row} onOpen={() => setOpenTgId(row.tgId)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openTgId !== null && (
        <UserDrawer tgId={openTgId} onClose={() => setOpenTgId(null)} />
      )}
    </div>
  );
}

function UserRow({ row, onOpen }: { row: AdminUserListRow; onOpen: () => void }) {
  return (
    <tr className="hover:bg-slate-900/40 cursor-pointer" onClick={onOpen}>
      <td className="px-3 py-2 font-mono text-xs">{row.tgId}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          {row.username
            ? <span className="text-slate-200">@{row.username}</span>
            : <span className="text-slate-500 italic">{row.firstName ?? 'no username'}</span>}
          {row.role === 'admin' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 border border-purple-800/50">admin</span>
          )}
          {!row.dm_opt_in && (
            <BellOff className="w-3 h-3 text-slate-500" aria-label="DM opt-out" />
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        <span className={cn(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          row.tier === 'pro' && 'bg-amber-900/40 text-amber-300 border border-amber-800/50',
          row.tier === 'reseller_pro' && 'bg-emerald-900/40 text-emerald-300 border border-emerald-800/50',
          (!row.tier || row.tier === 'free') && 'bg-slate-800 text-slate-400 border border-slate-700',
        )}>{row.tier ?? 'free'}</span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        <div>{row.prefs.categories.join(', ') || <span className="text-slate-600">—</span>}</div>
        <div className="text-slate-500">
          {row.prefs.budget?.label ?? '—'} · {row.prefs.dm_frequency ?? '—'}
          {' · '}
          <span className="text-slate-400" title="Effective daily DM cap (tier × dm_frequency)">
            {effectiveDmCap(row.tier, row.prefs.dm_frequency)} / day
          </span>
          {!row.prefs.onboardingComplete && (
            <span className="ml-1 text-[10px] text-amber-500">no flag</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">{row.deliveryCount}</td>
      <td className="px-3 py-2 text-right text-xs text-slate-400">
        {row.lastDeliveryAt ? relativeTime(row.lastDeliveryAt) : <span className="text-slate-600">never</span>}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">{row.feedbackCount30d}</td>
    </tr>
  );
}

function UserDrawer({ tgId, onClose }: { tgId: number; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', tgId],
    queryFn:  () => getAdminUser(tgId),
  });

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-slate-950 border-l border-slate-800 overflow-y-auto">
        <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium">tgId {tgId}</span>
            {data?.username && <span className="text-sm text-slate-400">· @{data.username}</span>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
          {isError && <div className="text-sm text-red-400">Failed to load.</div>}
          {data && (
            <>
              <ComingSoonActionRow />
              <DetailSection title="Preferences">
                <PrefsBlock detail={data} />
              </DetailSection>
              <DetailSection title="Signals (last 30 days)">
                <SignalsBlock signals={data.signals} />
              </DetailSection>
              <DetailSection title="Recent deliveries (last 10)">
                <DeliveriesBlock deliveries={data.recentDeliveries} />
              </DetailSection>
              <DetailSection title="Active cooldowns (Tony calibration #2)">
                <CooldownsBlock tgId={tgId} />
              </DetailSection>
              <DetailSection title="Recent events (last 20)">
                <EventsBlock events={data.recentEvents} />
              </DetailSection>
              <DetailSection title="Badges">
                <BadgesBlock badges={data.badges} />
              </DetailSection>
              {data.preferenceVector && (
                <DetailSection title="Preference vector">
                  <pre className="text-xs bg-slate-900/40 border border-slate-800 rounded p-3 overflow-x-auto">
                    {JSON.stringify(data.preferenceVector, null, 2)}
                  </pre>
                </DetailSection>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ComingSoonActionRow() {
  const Btn = ({ icon: Icon, label }: { icon: typeof Bell; label: string }) => (
    <button
      disabled
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-slate-800 text-slate-500 cursor-not-allowed"
      title="Coming soon"
    >
      <Icon className="w-3 h-3" /> {label}
      <span className="ml-1 text-[9px] uppercase tracking-wide text-slate-600">soon</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Btn icon={Bell} label="Resend welcome DM" />
      <Btn icon={Crown} label="Gift Pro tier" />
      <Btn icon={Gift} label="Award badge" />
      <Btn icon={RotateCw} label="Force re-onboard" />
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      {children}
    </div>
  );
}

function PrefsBlock({ detail }: { detail: AdminUserDetail }) {
  // 2026-05-10 — surface the resolved daily DM cap for this user.
  // Computed from tier × dm_frequency; matches what the fanout will use
  // when canSendDm runs.
  const cap = effectiveDmCap(detail.tier, detail.prefs.dm_frequency);
  return (
    <div className="rounded border border-slate-800 bg-slate-900/30 p-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
      <KV k="categories"  v={detail.prefs.categories.join(', ') || '—'} />
      <KV k="budget"      v={detail.prefs.budget?.label ?? '—'} />
      <KV k="dm_frequency" v={detail.prefs.dm_frequency ?? '—'} />
      <KV k="effective cap" v={`${cap} / day`} />
      <KV k="onboarded"   v={detail.prefs.onboardingComplete ? '✓' : '✗ (no flag)'} />
      <KV k="city"        v={detail.prefs.city ?? '—'} />
      <KV k="dm_opt_in"   v={detail.dm_opt_in ? '✓' : '✗'} />
      <KV k="created"     v={detail.createdAt ? new Date(detail.createdAt).toLocaleDateString() : '—'} />
      <KV k="updated"     v={detail.updatedAt ? new Date(detail.updatedAt).toLocaleDateString() : '—'} />
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-slate-500 min-w-[5rem]">{k}</span>
      <span className="text-slate-200 truncate">{v}</span>
    </div>
  );
}

/**
 * 2026-05-10 — Tony calibration #2 viewer. Lists all per-user
 * `cooldown:user:${tgId}:*` Redis records, freshest TTL first. Shows
 * the priceAtSend that gates each modelKey, hours of cooldown left, and
 * the implied "must drop below £X to send again" floor (10% drop rule).
 *
 * Read-only — admins cannot delete or modify cooldown state from here.
 */
function CooldownsBlock({ tgId }: { tgId: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['user-cooldowns', tgId],
    queryFn:  () => getUserCooldowns(tgId),
  });

  if (isLoading) {
    return <div className="text-xs text-slate-500"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading cooldowns…</div>;
  }
  if (isError) {
    return <div className="text-xs text-red-400">Failed to load cooldowns.</div>;
  }
  const records = data?.records ?? [];
  if (records.length === 0) {
    return <div className="text-xs text-slate-500">No active cooldowns.</div>;
  }
  return (
    <div className="rounded border border-slate-800 bg-slate-900/30 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/80 text-slate-400">
          <tr>
            <th className="text-left  px-2 py-1.5">modelKey</th>
            <th className="text-right px-2 py-1.5">£ at send</th>
            <th className="text-right px-2 py-1.5">override floor</th>
            <th className="text-right px-2 py-1.5">expires</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r: UserCooldownRecord) => {
            // 10% drop rule mirrors PRICE_DROP_THRESHOLD in cooldown.service.ts.
            const floor = Math.round(r.priceAtSend * 0.9);
            const hoursLeft = Math.max(0, Math.round(r.ttlSeconds / 3600));
            return (
              <tr key={r.modelKey} className="border-t border-slate-800">
                <td className="px-2 py-1.5 font-mono text-slate-200">{txt(r.modelKey)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                  £{r.priceAtSend.toFixed(0)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-500"
                    title="Current price must drop below this for the cooldown to be overridden">
                  &lt; £{floor}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">
                  {hoursLeft}h
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SignalsBlock({ signals }: { signals: AdminUserDetail['signals'] }) {
  const has = (m: Record<string, number>) => Object.keys(m).length > 0;
  const Row = ({ label, m }: { label: string; m: Record<string, number> }) => (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-slate-500 min-w-[6rem]">{label}</span>
      <span className="text-slate-200">
        {has(m) ? Object.entries(m).map(([k, n]) => `${k}:${n}`).join(' · ') : '—'}
      </span>
    </div>
  );
  return (
    <div className="rounded border border-slate-800 bg-slate-900/30 p-3 space-y-1">
      <Row label="🔥 like src"   m={signals.likes.sources} />
      <Row label="🔥 like cat"   m={signals.likes.categories} />
      <Row label="😐 meh src"    m={signals.meh.sources} />
      <Row label="😐 meh cat"    m={signals.meh.categories} />
      <Row label="👎 dislike src" m={signals.dislikes.sources} />
      <Row label="👎 dislike cat" m={signals.dislikes.categories} />
      <Row label="⏭ hide src"    m={signals.hides.sources} />
      <Row label="🛒 click src"  m={signals.clicks.sources} />
      <Row label="🛒 click cat"  m={signals.clicks.categories} />
      <div className="text-[10px] text-slate-500 pt-1 border-t border-slate-800">
        Aggregator counts events by `meta.kind` (post-2026-05-08 fix includes `meh`).
        Punitive bans (dislike/hide threshold ≥3) live in signals.service.
      </div>
    </div>
  );
}

function DeliveriesBlock({ deliveries }: { deliveries: AdminUserDetail['recentDeliveries'] }) {
  if (!deliveries.length) return <div className="text-xs text-slate-500">No deliveries.</div>;
  return (
    <div className="rounded border border-slate-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/40 text-slate-500">
          <tr>
            <th className="text-left px-2 py-1.5">when</th>
            <th className="text-left px-2 py-1.5">source</th>
            <th className="text-left px-2 py-1.5">cat</th>
            <th className="text-left px-2 py-1.5">model</th>
            <th className="text-right px-2 py-1.5">£</th>
            <th className="text-right px-2 py-1.5">DCS</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {deliveries.map((d, i) => (
            <tr key={i}>
              <td className="px-2 py-1.5 text-slate-400">{relativeTime(d.sent_at)}</td>
              <td className="px-2 py-1.5">{d.source ?? '—'}</td>
              <td className="px-2 py-1.5">{txt(d.category)}</td>
              <td className="px-2 py-1.5 font-mono text-[10px] truncate max-w-[10rem]">{d.model_key ?? d.deal_id.slice(0, 12)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{d.deal_price ?? '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{d.dcs_score ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventsBlock({ events }: { events: AdminUserDetail['recentEvents'] }) {
  if (!events.length) return <div className="text-xs text-slate-500">No events.</div>;
  return (
    <div className="rounded border border-slate-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/40 text-slate-500">
          <tr>
            <th className="text-left px-2 py-1.5">when</th>
            <th className="text-left px-2 py-1.5">type</th>
            <th className="text-left px-2 py-1.5">summary</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {events.map((e, i) => (
            <tr key={i}>
              <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">{relativeTime(e.ts)}</td>
              <td className="px-2 py-1.5 font-mono text-[10px]">{e.type}</td>
              <td className="px-2 py-1.5 font-mono text-[10px] truncate max-w-[28rem]">
                {summariseEvent(e.type, e.meta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function summariseEvent(type: string, meta: any): string {
  if (!meta) return '';
  if (type === 'deal_feedback') {
    const kind = meta.kind ?? '(no kind)';
    const sub  = meta.subkind ?? '';
    return `${kind}${sub && kind !== sub ? `/${sub}` : ''} · ${meta.source ?? ''}/${meta.category ?? ''} · ${meta.model_key ?? ''}`;
  }
  if (type === 'deal_clicked') return `${meta.source ?? ''} · ${meta.uniqueId?.slice?.(0, 12) ?? ''}`;
  // Fallback: show truncated JSON
  const s = JSON.stringify(meta);
  return s.length > 120 ? s.slice(0, 117) + '…' : s;
}

function BadgesBlock({ badges }: { badges: AdminUserDetail['badges'] }) {
  if (!badges.length) return <div className="text-xs text-slate-500">No badges.</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((b, i) => (
        <div key={i} className="text-xs px-2 py-1 rounded border border-slate-800 bg-slate-900/40 font-mono">
          {b.badgeId}
          {b.awardedAt && <span className="ml-1 text-slate-500">{new Date(b.awardedAt).toLocaleDateString()}</span>}
        </div>
      ))}
    </div>
  );
}
